import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  courseAppearanceService,
  type CreateCourseMenuItemInput,
  type CourseMenuItem,
} from '@/services/course-appearance.service';
import { appearanceKeys } from './queryKeys';

/** Create a per-course menu item, optimistically appended. */
export function useCreateCourseMenuItem(productId: string) {
  const qc = useQueryClient();
  const key = appearanceKeys.menuItems(productId);

  return useMutation({
    mutationFn: (input: CreateCourseMenuItemInput) =>
      courseAppearanceService.createMenuItem(productId, input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<CourseMenuItem[]>(key);
      const now = new Date().toISOString();
      const temp: CourseMenuItem = {
        id: `temp-${Date.now()}`,
        productId,
        title: input.title,
        icon: input.icon,
        url: input.url,
        isExternal: input.isExternal ?? false,
        openNewTab: input.openNewTab ?? false,
        order: input.order ?? prev?.length ?? 0,
        isActive: input.isActive ?? true,
        createdAt: now,
        updatedAt: now,
      };
      qc.setQueryData<CourseMenuItem[]>(key, [...(prev ?? []), temp]);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(i18n.t('adminAppearance:toasts.createMenuItemError'));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
